import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { RefreshCw, UserCheck, Package, Trash2, PowerOff, Power, PlusCircle, Edit } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const NewCustomerFields = ({ form, setForm, allBasePacks, moviesPacks, vodPacks, adultPacks }) => (
  <>
    <div className="form-group">
      <label>Subscriber Name *</label>
      <input className="form-control" placeholder="Enter the customer's full name" required value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} />
    </div>
    <div className="form-group">
      <label>Phone Number</label>
      <input className="form-control" placeholder="Phone (optional)" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
    </div>
    <div className="form-group">
      <label>Base Package *</label>
      <select className="form-control" value={form.package_id} onChange={e => setForm({...form, package_id: e.target.value})} required>
        <option value="">-- Select Package --</option>
        {allBasePacks.map(p => <option key={p.id} value={p.id}>{p.name} — {p.validity_label || '1 Month'} — {p.channel_count || 0} channels</option>)}
      </select>
    </div>
    {moviesPacks.length > 0 && (
      <div className="form-group">
        <label>🎬 Movies Pack Add-on</label>
        <select className="form-control" value={form.movies_addon_id} onChange={e => setForm({...form, movies_addon_id: e.target.value})}>
          <option value="">-- No Movies Pack --</option>
          {moviesPacks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
    )}
    {vodPacks.length > 0 && (
      <div className="form-group">
        <label>📹 VOD Pack Add-on</label>
        <select className="form-control" value={form.vod_addon_id || ''} onChange={e => setForm({...form, vod_addon_id: e.target.value})}>
          <option value="">-- No VOD Pack --</option>
          {vodPacks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
    )}
    {adultPacks.length > 0 && (
      <div className="form-group">
        <label>🔞 Adult Pack Add-on</label>
        <select className="form-control" value={form.addon_package_id} onChange={e => setForm({...form, addon_package_id: e.target.value})}>
          <option value="">-- No Adult Pack --</option>
          {adultPacks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {form.addon_package_id && (
          <div style={{ marginTop: '8px' }}>
            <input className="form-control" placeholder="4-digit Adult PIN" maxLength={4} value={form.adult_pin} onChange={e => setForm({...form, adult_pin: e.target.value.replace(/\D/,'')})} />
          </div>
        )}
      </div>
    )}
  </>
);
const Subscribers = () => {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [active, setActive] = useState([]);
  const [expired, setExpired] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [refreshing, setRefreshing] = useState(false);

  // Modals
  const [showActivate, setShowActivate] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [showAddAddon, setShowAddAddon] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const [selectedMac, setSelectedMac] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);

  const [form, setForm] = useState({
    full_name: '', phone: '', package_id: '',
    months: 1, adult_enabled: false, adult_pin: '',
    movies_addon_id: '', addon_package_id: '', vod_addon_id: ''
  });

  const [addonForm, setAddonForm] = useState({ package_id: '', months: 1, adult_pin: '' });
  const [activateMode, setActivateMode] = useState('new');
  const [existingUserId, setExistingUserId] = useState('');
  const [existingSearchTerm, setExistingSearchTerm] = useState('');
  const [endUsers, setEndUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSetPin, setShowSetPin] = useState(false);
  const [setPinValue, setSetPinValue] = useState('');
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewTarget, setRenewTarget] = useState(null);
  const [renewPackageId, setRenewPackageId] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [historySubject, setHistorySubject] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(() => fetchPending(true), 30000);
    return () => clearInterval(interval);
  }, [selectedTenantId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const tenantParam = selectedTenantId ? `?tenant_id=${selectedTenantId}` : '';
      const [pendingRes, subsRes, pkgsRes, expiredRes, usersRes] = await Promise.all([
        api.get('/api/pending/list' + (selectedTenantId ? '?tenant_id=' + selectedTenantId : '')),
        api.get(`/api/subscriptions${tenantParam}`),
        api.get('/api/packages'),
        api.get(`/api/subscriptions/expired-devices${tenantParam}`).catch(() => ({data:{data:[]}})),
        api.get('/api/users').catch(() => ({data:{data:[]}})),
      ]);
      setPending(pendingRes.data.data || []);
      setExpired(expiredRes.data.data || []);
      setActive(subsRes.data.data || []);
      setPackages(pkgsRes.data.data || []);
      const allUsers = usersRes.data.data || [];
      setEndUsers(allUsers.filter(u => u.role === 'enduser'));
      setTenants(allUsers.filter(u => u.role === 'distributor' || u.role === 'lco'));
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const fetchPending = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await api.get('/api/pending/list' + (selectedTenantId ? '?tenant_id=' + selectedTenantId : ''));
      setPending(res.data.data || []);
    } catch {}
    finally { if (!silent) setRefreshing(false); }
  };

  const handleDirectRenew = async () => {
    if (!renewPackageId) { toast.error('Select a package'); return; }
    try {
      await api.patch(`/api/pending/${renewTarget.mac_address}/activate`, {
        user_id: renewTarget.user_id, package_id: renewPackageId
      });
      toast.success('Renewed successfully');
      setShowRenewModal(false);
      setRenewTarget(null);
      setRenewPackageId('');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.message || 'Renew failed'); }
  };
  const openHistory = async (g) => {
    setHistorySubject(g);
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/api/subscriptions/user/${g.user_id}/history`);
      setHistoryData(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load history');
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };
  const handleRenewPackage = async (pkg, mac) => {
    try {
      await api.post('/api/subscriptions', { user_id: pkg.sub.user_id, mac_address: mac, package_id: pkg.sub.package_id, months: 1 });
      toast.success('Renewed: ' + (pkg.name || 'package'));
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.message || 'Renew failed'); }
  };
  // ===== ACTIVATE =====
  const openActivate = (mac) => {
    setSelectedMac(mac);
    setForm({ full_name: '', phone: '', package_id: '', months: 1, adult_enabled: false, adult_pin: '', movies_addon_id: '', addon_package_id: '', vod_addon_id: '' });
    setActivateMode('new');
    setExistingUserId('');
    setShowActivate(true);
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    if (activateMode === 'existing') {
      if (!existingUserId) { toast.error('Please select the existing customer'); return; }
      try {
        await api.patch(`/api/pending/${selectedMac.mac_address}/activate`, {
          user_id: existingUserId,
        });
        toast.success('Device linked - existing subscription carries over, no new validity started');
        setShowActivate(false);
        fetchAll();
      } catch (err) { toast.error(err.response?.data?.message || 'Failed to link device'); }
      return;
    }
    if (!form.full_name || !form.full_name.trim()) { toast.error('Please enter the subscriber name'); return; }
    if (!form.package_id) { toast.error('Please select a base package'); return; }
    if (form.adult_enabled && form.adult_pin.length !== 4) { toast.error('Adult PIN must be 4 digits'); return; }
    try {
      const userRes = await api.post('/api/users', {
        username: `user_${selectedMac.mac_address.replace(/:/g,'').toLowerCase().slice(-6)}_${Date.now().toString().slice(-4)}`,
        password: Math.random().toString(36).slice(-8),
        full_name: form.full_name.trim(),
        phone: form.phone,
        role: 'enduser',
        adult_enabled: form.adult_enabled,
        adult_pin: form.adult_pin,
      });
      const userId = userRes.data.data.id;

      await api.patch(`/api/pending/${selectedMac.mac_address}/activate`, {
        user_id: userId,
        package_id: form.package_id,
      });

      // Add movies addon if selected
      if (form.movies_addon_id) {
        const movPkg = packages.find(p => p.id === form.movies_addon_id);
        const movDays = movPkg?.validity_days || 30;
        const movEnd = new Date();
        movEnd.setDate(movEnd.getDate() + movDays);
        await api.post('/api/subscriptions', {
          user_id: userId, mac_address: selectedMac.mac_address,
          package_id: form.movies_addon_id, end_date: movEnd
        }).catch(() => {});
      }

      // Add adult addon if selected
      if (form.addon_package_id) {
        const adultPkg = packages.find(p => p.id === form.addon_package_id);
        const adultDays = adultPkg?.validity_days || 30;
        const adultEnd = new Date();
        adultEnd.setDate(adultEnd.getDate() + adultDays);
        await api.post('/api/subscriptions', {
          user_id: userId, mac_address: selectedMac.mac_address,
          package_id: form.addon_package_id, end_date: adultEnd
        }).catch(() => {});
      }

      toast.success('Subscriber activated!');
      setShowActivate(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Activation failed');
    }
  };

  // ===== MANAGE =====
  const openManage = (sub) => {
    setSelectedSub(sub);
    setShowManage(true);
  };

  // ===== ADD ADDON =====
  const openAddAddon = (sub) => {
    setSelectedSub(sub);
    setAddonForm({ package_id: '', months: 1, adult_pin: '' });
    setShowAddAddon(true);
  };

  const handleAddAddon = async (e) => {
    e.preventDefault();
    if (!addonForm.package_id) { toast.error('Select a package'); return; }
    const isAdultPack = adultPacks.some(p => p.id === addonForm.package_id);
    if (isAdultPack && addonForm.adult_pin.length !== 4) { toast.error('Adult PIN must be 4 digits'); return; }
    try {
      await api.post('/api/subscriptions', {
        user_id: selectedSub.user_id,
        mac_address: selectedSub.mac_address,
        package_id: addonForm.package_id,
        months: addonForm.months,
      });
      if (isAdultPack) {
        await api.put(`/api/users/${selectedSub.user_id}/adult-pin`, { pin: addonForm.adult_pin });
      }
      toast.success('Add-on activated!');
      setShowAddAddon(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  // ===== DEACTIVATE / REACTIVATE =====
  const handleToggleActive = async (sub) => {
    setConfirmAction({
      title: sub.is_active ? 'Deactivate Subscription' : 'Reactivate Subscription',
      message: sub.is_active
        ? `Deactivate ${sub.full_name || sub.username}'s subscription? They will lose access immediately.`
        : `Reactivate ${sub.full_name || sub.username}'s subscription?`,
      confirm: sub.is_active ? 'Deactivate' : 'Reactivate',
      color: sub.is_active ? '#f75f5f' : '#00d4aa',
      action: async () => {
        try {
          await api.put(`/api/subscriptions/${sub.id}/toggle`, {});
          toast.success(sub.is_active ? 'Subscription deactivated' : 'Subscription reactivated');
          fetchAll();
        } catch { toast.error('Failed'); }
      }
    });
    setShowConfirm(true);
  };

  // ===== DELETE USER =====
  const handleDeleteUser = async (sub) => {
    setConfirmAction({
      title: 'Delete Subscriber',
      message: `Permanently delete ${sub.full_name || sub.username}? This cannot be undone. All subscriptions and devices will be removed.`,
      confirm: 'Delete Permanently',
      color: '#f75f5f',
      action: async () => {
        try {
          await api.delete(`/api/users/${sub.user_id}`);
          toast.success('Subscriber deleted');
          fetchAll();
        } catch { toast.error('Failed to delete'); }
      }
    });
    setShowConfirm(true);
  };

  const basePacks = packages.filter(p => !p.is_adult_pack && !p.is_demo && !p.is_movies_pack);
  const adultPacks = packages.filter(p => p.is_adult_pack);
  const moviesPacks = packages.filter(p => p.is_movies_pack);
  const demoPacks = packages.filter(p => p.is_demo);
  const allBasePacks = [...basePacks, ...demoPacks];
  const vodPacks = packages.filter(p => p.is_vod_pack);
  const addonPacks = [...adultPacks, ...moviesPacks, ...vodPacks];

  const validityOptions = [
    { label: '1 Month', days: 30, months: 1 },
    { label: '3 Months', days: 90, months: 3 },
    { label: '6 Months', days: 180, months: 6 },
    { label: '12 Months', days: 365, months: 12 },
  ];

  if (loading) return <div className="loading"><div className="spinner" />Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Subscribers</div>
          <div className="page-subtitle">{pending.length} pending · {new Set(active.map(s=>s.mac_address).filter(Boolean)).size} active</div>
        </div>
        <button onClick={() => fetchAll()} className="btn btn-secondary" disabled={refreshing}>
          <RefreshCw size={14} className={refreshing ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {[
          { key: 'pending', label: `⏳ Pending (${pending.length})` },
          { key: 'active', label: `✅ Active (${new Set(active.map(s=>s.mac_address)).size})` },
          { key: 'expired', label: `🔴 Expired (${expired.length})` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className="btn" style={{
            background: tab === t.key ? 'var(--accent-blue)' : 'var(--bg-secondary)',
            color: tab === t.key ? '#fff' : 'var(--text-secondary)',
            borderColor: tab === t.key ? 'var(--accent-blue)' : 'var(--border)',
          }}>{t.label}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <input className="form-control" placeholder="Search by name, phone, username, or MAC address..."
          value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ maxWidth: '400px' }} />
        {user?.role === 'superadmin' && (
          <select className="form-control" value={selectedTenantId} onChange={e => setSelectedTenantId(e.target.value)} style={{ maxWidth: '260px' }}>
            <option value="">-- All Tenants --</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name || t.username} ({t.role})</option>)}
          </select>
        )}
      </div>

      {/* Pending Tab */}
      {tab === 'pending' && (
        <div className="card">
          {pending.length === 0 ? (
            <div className="empty-state">No pending devices</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>MAC Address</th>
                  <th>Operator Code</th>
                  <th>Registered At</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pending.map(p => (
                  <tr key={p.mac_address}>
                    <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>{p.mac_address}</td>
                    <td><span className="badge badge-info">{p.operator_code}</span></td>
                    <td style={{ fontSize: '13px' }}>{new Date(p.created_at).toLocaleString('en-IN')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {user?.role === 'superadmin' && (p.operator_name || p.operator_username) && (
                          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', alignSelf: 'center', marginRight: '4px' }}>via {p.operator_name || p.operator_username}</span>
                        )}
                        <button disabled={user?.role === 'superadmin' && p.operator_id !== user?.id} title={user?.role === 'superadmin' && p.operator_id !== user?.id ? "Activate from the operator's own account" : undefined} onClick={() => openActivate(p)} className="btn btn-success" style={{ fontSize: '13px' }}>
                          <UserCheck size={13} /> Activate
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm('Delete this pending device?')) return;
                          try {
                            await api.delete('/api/pending/' + p.mac_address);
                            setPending(prev => prev.filter(d => d.mac_address !== p.mac_address));
                          } catch { alert('Failed to delete'); }
                        }} className="btn btn-danger" style={{ fontSize: '13px' }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Active Tab */}
      {tab === 'expired' && (
        <div className="table-container">
          {expired.length === 0 ? (
            <div style={{textAlign:'center',padding:'40px',color:'var(--text-secondary)'}}>No expired devices</div>
          ) : (
            <table>
              <thead><tr>
                <th>Subscriber</th>
                <th>MAC Address</th>
                <th>Last Expiry</th>
                <th>Action</th>
              </tr></thead>
              <tbody>
                {expired.filter(d => {
                  const term = searchTerm.toLowerCase();
                  return !term ||
                    (d.full_name||'').toLowerCase().includes(term) ||
                    (d.username||'').toLowerCase().includes(term) ||
                    (d.phone||'').toLowerCase().includes(term) ||
                    (d.mac_address||'').toLowerCase().includes(term);
                }).map(d => (
                  <tr key={d.device_id}>
                    <td>{d.full_name || d.username || '—'}</td>
                    <td style={{fontFamily:'monospace'}}>{d.mac_address}</td>
                    <td style={{color:'#FF4757'}}>{d.last_expiry ? new Date(d.last_expiry).toLocaleDateString() : 'Never activated'}</td>
                    <td>
                      {d.user_id ? (
                        <button className="btn btn-primary" style={{padding:'4px 12px',fontSize:'12px'}}
                          onClick={() => { setRenewTarget(d); setRenewPackageId(''); setShowRenewModal(true); }}>
                          Renew
                        </button>
                      ) : (
                        <button className="btn btn-primary" style={{padding:'4px 12px',fontSize:'12px'}}
                          onClick={() => { setSelectedMac({mac_address: d.mac_address}); setShowActivate(true); }}>
                          Activate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {tab === 'active' && (
        <div className="card">
          {active.length === 0 ? (
            <div className="empty-state">No active subscribers</div>
          ) : (() => {
            // Group subscriptions by MAC address
            const term = searchTerm.toLowerCase();
            const filteredActive = active.filter(s => !term ||
              (s.full_name||'').toLowerCase().includes(term) ||
              (s.username||'').toLowerCase().includes(term) ||
              (s.phone||'').toLowerCase().includes(term) ||
              (s.mac_address||'').toLowerCase().includes(term));
            const grouped = {};
            filteredActive.forEach(s => {
              const mac = s.mac_address || 'unknown';
              if (!grouped[mac]) grouped[mac] = { ...s, packages: [] };
              // Avoid duplicate packages
              const exists = grouped[mac].packages.find(p => p.name === s.package_name);
              if (!exists) grouped[mac].packages.push({ id: s.id, name: s.package_name, end_date: s.end_date, is_active: s.is_active, sub: s });
            });
            return (
            <table>
              <thead>
                <tr>
                  <th>Subscriber</th>
                  <th>MAC Address</th>
                  <th>Packages</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(grouped).map(g => {
                  const mainSub = g.packages.find(p => !p.name?.includes('Adult') && !p.name?.includes('Movie') && !p.name?.includes('VOD')) || g.packages[0];
                  const hasActivePkg = g.packages.some(p => p.sub.is_active && new Date(p.end_date) > new Date());
                  return (
                    <tr key={g.mac_address}>
                      <td>
                        <div onClick={() => openHistory(g)} style={{ fontWeight: '600', cursor: 'pointer', color: 'var(--accent-blue)', textDecoration: 'underline' }} title="Click to view full activation history">{g.full_name || g.username || '—'}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{g.phone || ''}</div>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{g.mac_address || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {g.packages
                            .filter(p => !g.packages.some(other => other.sub.package_id === p.sub.package_id && new Date(other.end_date) > new Date(p.end_date)))
                            .map(p => {
                            const pkgExpired = new Date(p.end_date) <= new Date();
                            return (
                              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span className="badge badge-info" style={{ fontSize: '11px' }}>{p.name || '—'}</span>
                                <span style={{ fontSize: '11px', color: pkgExpired ? '#f75f5f' : 'var(--text-secondary)' }}>
                                  {p.end_date ? new Date(p.end_date).toLocaleDateString('en-IN') : '—'}
                                  {pkgExpired && ' (Exp)'}
                                </span>
                                {pkgExpired && (
                                  <button onClick={() => handleRenewPackage(p, g.mac_address)} className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: '10px' }}>Renew</button>
                                )}
                                <button onClick={() => handleToggleActive(p.sub)} className="btn btn-secondary" style={{ padding: '2px 6px' }} title={p.sub.is_active ? 'Deactivate this package' : 'Activate this package'}>
                                  {p.sub.is_active ? <PowerOff size={11} /> : <Power size={11} />}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${hasActivePkg ? 'badge-success' : 'badge-danger'}`}>
                          {hasActivePkg ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button onClick={() => openManage(mainSub?.sub || g)} className="btn" style={{ fontSize: '12px', padding: '4px 8px' }} title="Manage">
                            <Edit size={12} /> Manage
                          </button>
                          <button onClick={() => openAddAddon(mainSub?.sub || g)} className="btn btn-secondary" style={{ fontSize: '12px', padding: '4px 8px' }} title="Add Package">
                            <PlusCircle size={12} /> Add-on
                          </button>
                          {user?.role === 'superadmin' && (
                            <button onClick={() => handleDeleteUser(mainSub?.sub || g)} className="btn" style={{ fontSize: '12px', padding: '4px 8px', background: 'rgba(247,95,95,0.1)', borderColor: '#f75f5f', color: '#f75f5f' }}>
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            );
          })()}
        </div>
      )}

      {/* Activate Modal */}
      {showActivate && (
        <div className="modal-overlay" onClick={() => setShowActivate(false)}>
          <div className="modal" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Activate Subscriber</div>
              <button className="modal-close" onClick={() => setShowActivate(false)}>×</button>
            </div>
            <form onSubmit={handleActivate}>
              <div className="modal-body">
                <div style={{ background: 'rgba(0,212,170,0.08)', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontFamily: 'monospace', fontSize: '14px', color: 'var(--accent-teal)' }}>
                  📡 MAC: {selectedMac?.mac_address}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  <button type="button" onClick={() => setActivateMode('new')} className="btn" style={{ flex: 1, background: activateMode === 'new' ? 'var(--accent-blue)' : 'var(--bg-card)', color: activateMode === 'new' ? 'white' : 'var(--text-secondary)', border: '1px solid var(--border)', padding: '8px' }}>New Customer</button>
                  <button type="button" onClick={() => setActivateMode('existing')} className="btn" style={{ flex: 1, background: activateMode === 'existing' ? 'var(--accent-blue)' : 'var(--bg-card)', color: activateMode === 'existing' ? 'white' : 'var(--text-secondary)', border: '1px solid var(--border)', padding: '8px' }}>Existing Customer</button>
                </div>
                {activateMode === 'existing' && (
                  <div className="form-group">
                    <label>Select Existing Customer *</label>
                    <input className="form-control" placeholder="Type name or phone to search..." value={existingSearchTerm}
                      onChange={e => setExistingSearchTerm(e.target.value)} style={{ marginBottom: '8px' }} />
                    <select className="form-control" value={existingUserId} onChange={e => setExistingUserId(e.target.value)} required size={existingSearchTerm ? 6 : 1}>
                      <option value="">-- Select customer --</option>
                      {endUsers
                        .filter(u => {
                          if (!existingSearchTerm) return true;
                          const t = existingSearchTerm.toLowerCase();
                          return (u.full_name||'').toLowerCase().includes(t) || (u.username||'').toLowerCase().includes(t) || (u.phone||'').toLowerCase().includes(t);
                        })
                        .map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}{u.phone ? ` — ${u.phone}` : ''}</option>)}
                    </select>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px' }}>
                      This links the device to the selected customer's existing account. Their current active subscription (if any) carries over as-is — no new validity period starts, and no new package is assigned here.
                    </div>
                  </div>
                )}
                {activateMode !== 'existing' && (
                  <NewCustomerFields form={form} setForm={setForm} allBasePacks={allBasePacks} moviesPacks={moviesPacks} vodPacks={vodPacks} adultPacks={adultPacks} />
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowActivate(false)}>Cancel</button>
                <button type="submit" className="btn btn-success">✓ Activate</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Modal */}
      {showManage && selectedSub && (
        <div className="modal-overlay" onClick={() => setShowManage(false)}>
          <div className="modal" style={{ maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Manage — {selectedSub.full_name || selectedSub.username}</div>
              <button className="modal-close" onClick={() => setShowManage(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div className="card" style={{ padding: '12px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Package</div>
                  <div style={{ fontWeight: '600', marginTop: '4px' }}>{selectedSub.package_name}</div>
                </div>
                <div className="card" style={{ padding: '12px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Expiry</div>
                  <div style={{ fontWeight: '600', marginTop: '4px', fontSize: '13px' }}>
                    {selectedSub.end_date ? new Date(selectedSub.end_date).toLocaleDateString('en-IN') : '—'}
                  </div>
                </div>
                <div className="card" style={{ padding: '12px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>MAC Address</div>
                  <div style={{ fontWeight: '600', marginTop: '4px', fontSize: '12px', fontFamily: 'monospace' }}>{selectedSub.mac_address}</div>
                </div>
                <div className="card" style={{ padding: '12px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Status</div>
                  <div style={{ marginTop: '4px' }}>
                    <span className={`badge ${selectedSub.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {selectedSub.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button onClick={() => { setSetPinValue(''); setShowSetPin(true); }} className="btn" style={{ justifyContent: 'center' }}>
                  🔞 Set / Change Adult PIN
                </button>
                <button onClick={() => { setShowManage(false); openAddAddon(selectedSub); }} className="btn" style={{ justifyContent: 'center' }}>
                  <PlusCircle size={14} /> Add Package / Renew
                </button>
                <button onClick={() => { setShowManage(false); handleToggleActive(selectedSub); }} className="btn" style={{ justifyContent: 'center', background: selectedSub.is_active ? 'rgba(247,95,95,0.1)' : 'rgba(0,212,170,0.1)', borderColor: selectedSub.is_active ? '#f75f5f' : '#00d4aa', color: selectedSub.is_active ? '#f75f5f' : '#00d4aa' }}>
                  {selectedSub.is_active ? <><PowerOff size={14} /> Deactivate Subscription</> : <><Power size={14} /> Reactivate Subscription</>}
                </button>
                {user?.role === 'superadmin' && (
                  <button onClick={() => { setShowManage(false); handleDeleteUser(selectedSub); }} className="btn" style={{ justifyContent: 'center', background: 'rgba(247,95,95,0.1)', borderColor: '#f75f5f', color: '#f75f5f' }}>
                    <Trash2 size={14} /> Delete Subscriber Permanently
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Addon Modal */}
      {showAddAddon && selectedSub && (
        <div className="modal-overlay" onClick={() => setShowAddAddon(false)}>
          <div className="modal" style={{ maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Add Package — {selectedSub.full_name || selectedSub.username}</div>
              <button className="modal-close" onClick={() => setShowAddAddon(false)}>×</button>
            </div>
            <form onSubmit={handleAddAddon}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Select Package</label>
                  <select className="form-control" value={addonForm.package_id} onChange={e => setAddonForm({...addonForm, package_id: e.target.value})} required>
                    <option value="">-- Select Package --</option>
                    <optgroup label="Base Packages">
                      {allBasePacks.map(p => <option key={p.id} value={p.id}>{p.name} — {p.channel_count} ch</option>)}
                    </optgroup>
                    {moviesPacks.length > 0 && <optgroup label="🎬 Movies Packs">
                      {moviesPacks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </optgroup>}
                    {adultPacks.length > 0 && <optgroup label="🔞 Adult Packs">
                      {adultPacks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </optgroup>}
                  </select>
                </div>
                <div className="form-group">
                  <label>Validity</label>
                  <select className="form-control" value={addonForm.months} onChange={e => setAddonForm({...addonForm, months: parseInt(e.target.value)})}>
                    {validityOptions.map(v => <option key={v.months} value={v.months}>{v.label}</option>)}
                  </select>
                </div>
                {adultPacks.some(p => p.id === addonForm.package_id) && (
                  <div className="form-group">
                    <label>4-digit Adult PIN *</label>
                    <input className="form-control" placeholder="4-digit Adult PIN" maxLength={4} value={addonForm.adult_pin}
                      onChange={e => setAddonForm({...addonForm, adult_pin: e.target.value.replace(/[^0-9]/g, '')})} />
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                      This PIN will be required on the customer's device before adult content can be viewed.
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddAddon(false)}>Cancel</button>
                <button type="submit" className="btn btn-success">✓ Add Package</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirm && confirmAction && (
        <div className="modal-overlay" onClick={() => setShowConfirm(false)}>
          <div className="modal" style={{ maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{confirmAction.title}</div>
              <button className="modal-close" onClick={() => setShowConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>{confirmAction.message}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn" style={{ background: confirmAction.color, borderColor: confirmAction.color, color: '#fff' }}
                onClick={() => { confirmAction.action(); setShowConfirm(false); }}>
                {confirmAction.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
      {showHistory && historySubject && (
        <div className="modal-overlay" onClick={() => setShowHistory(false)}>
          <div className="modal" style={{ maxWidth: '640px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Activation History — {historySubject.full_name || historySubject.username}</div>
              <button className="modal-close" onClick={() => setShowHistory(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ background: 'rgba(0,212,170,0.08)', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px' }}>
                <div><strong>MAC:</strong> <span style={{ fontFamily: 'monospace' }}>{historySubject.mac_address || '—'}</span></div>
                <div><strong>Phone:</strong> {historySubject.phone || '—'}</div>
              </div>
              {historyLoading ? (
                <div className="loading"><div className="spinner" />Loading...</div>
              ) : historyData.length === 0 ? (
                <div className="empty-state">No activation history found</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '400px', overflowY: 'auto' }}>
                  {historyData.map(h => {
                    const isExpired = new Date(h.end_date) <= new Date();
                    return (
                      <div key={h.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <span style={{ fontWeight: '600' }}>{h.package_name || 'Unknown Package'}</span>
                          <span className={`badge ${h.is_active && !isExpired ? 'badge-success' : 'badge-danger'}`}>
                            {h.is_active && !isExpired ? 'Active' : 'Expired'}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                          {new Date(h.start_date).toLocaleDateString('en-IN')} → {new Date(h.end_date).toLocaleDateString('en-IN')}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          Activated by: <strong>{h.activated_by_name || '—'}</strong>
                        </div>
                        {h.charged_amount != null && (
                          <div style={{ fontSize: '12px', color: '#00d4aa', marginTop: '4px' }}>
                            💰 Charged ₹{parseFloat(h.charged_amount).toFixed(2)} — {h.charge_description}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {showRenewModal && renewTarget && (
        <div className="modal-overlay" onClick={() => setShowRenewModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Renew — {renewTarget.full_name || renewTarget.username}</div>
              <button className="modal-close" onClick={() => setShowRenewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                MAC: <span style={{ fontFamily: 'monospace' }}>{renewTarget.mac_address}</span>
              </div>
              <div className="form-group">
                <label>Package *</label>
                <select className="form-control" value={renewPackageId} onChange={e => setRenewPackageId(e.target.value)}>
                  <option value="">-- Select Package --</option>
                  {allBasePacks.map(p => <option key={p.id} value={p.id}>{p.name} — {p.validity_label || '1 Month'}</option>)}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowRenewModal(false)}>Cancel</button>
              <button className="btn btn-success" onClick={handleDirectRenew}>Renew</button>
            </div>
          </div>
        </div>
      )}
      {showSetPin && selectedSub && (
        <div className="modal-overlay" onClick={() => setShowSetPin(false)}>
          <div className="modal" style={{ maxWidth: '380px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Set Adult PIN — {selectedSub.full_name || selectedSub.username}</div>
              <button className="modal-close" onClick={() => setShowSetPin(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>4-digit PIN *</label>
                <input className="form-control" placeholder="4-digit PIN" maxLength={4} value={setPinValue}
                  onChange={e => setSetPinValue(e.target.value.replace(/\D/,''))} />
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                  This PIN will be required on the customer's device before adult content can be viewed.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowSetPin(false)}>Cancel</button>
              <button className="btn btn-success" onClick={async () => {
                if (setPinValue.length !== 4) { toast.error('PIN must be 4 digits'); return; }
                try {
                  await api.put(`/api/users/${selectedSub.user_id}/adult-pin`, { pin: setPinValue });
                  toast.success('PIN saved');
                  setShowSetPin(false);
                } catch (err) { toast.error(err.response?.data?.message || 'Failed to save PIN'); }
              }}>Save PIN</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscribers;
